/**
 * Телефон: канон хранения E.164 (+…), парсинг/валидация через libphonenumber.
 * Маска +7 (XXX)… — только для legacy mobile / RN; веб переводим на country picker + E.164.
 */

import {
  Metadata,
  parsePhoneNumber,
  parsePhoneNumberFromString,
  isValidPhoneNumber,
  isPossiblePhoneNumber,
  validatePhoneNumberLength,
  parseIncompletePhoneNumber,
  formatIncompletePhoneNumber,
  getCountries,
  getCountryCallingCode,
  isSupportedCountry,
  type CountryCode,
} from 'libphonenumber-js';
import { phoneCountryLabels } from './phone-country-labels.js';

export type { CountryCode };

export type PhoneCountryOption = {
  value: CountryCode;
  label: string;
  callingCode: string;
};

const phoneCountryOptionsCache = new Map<string, PhoneCountryOption[]>();

/** ITU-T E.164: не более 15 цифр в номере (всего, с кодом страны). */
export const E164_MAX_DIGITS_TOTAL = 15;

const metadataPossibleNationalLengthsCache = new Map<CountryCode, number[]>();

function getSortedPossibleNationalLengths(country: CountryCode): number[] {
  if (!isSupportedCountry(country)) return [];
  const hit = metadataPossibleNationalLengthsCache.get(country);
  if (hit) return hit;
  const m = new Metadata();
  m.selectNumberingPlan(country);
  const raw = m.numberingPlan?.possibleLengths();
  const sorted =
    raw && raw.length > 0 ? [...new Set(raw)].sort((a, b) => a - b) : [];
  metadataPossibleNationalLengthsCache.set(country, sorted);
  return sorted;
}

function legacyMaxNationalFromTooLongProbe(country: CountryCode, maxByE164: number): number {
  const countryCallingCode = getCountryCallingCode(country);
  let maxLen = maxByE164;
  for (let len = 1; len <= maxByE164; len += 1) {
    const probe = `+${countryCallingCode}${'0'.repeat(len)}`;
    if (validatePhoneNumberLength(probe) === 'TOO_LONG') {
      maxLen = Math.max(1, len - 1);
      break;
    }
  }
  return maxLen;
}

/**
 * Обрезает ввод до лимита E.164, чтобы нельзя было набрать бесконечно цифр.
 * Возвращает E.164 с + при переполнении; иначе исходную строку (короткий ввод не ломаем).
 */
export function clampPhoneToE164Max(phone: string): string {
  const t = phone.trim();
  if (!t) return '';
  const digits = t.replace(/\D/g, '');
  if (digits.length <= E164_MAX_DIGITS_TOTAL) return phone;
  return `+${digits.slice(0, E164_MAX_DIGITS_TOTAL)}`;
}

/**
 * Максимум цифр национальной части (без country calling code) для ограничения ввода.
 * Источник — `possibleLengths` из метаданных libphonenumber (как `limitMaxLength` / trimNumber в react-phone-number-input).
 *
 * Для RU/KZ в метаданных два допустимых размера (10 и 14): «длинный» набор только с ведущей 8 в национальной части;
 * иначе лимит как у обычного мобильного/фикс (10), иначе можно набрать лишние цифры и получить только ошибку валидации.
 *
 * @param partialNationalDigits уже только национальные цифры (без кода страны), могут быть неполными
 */
export function getMaxNationalLengthForCountry(country: CountryCode, partialNationalDigits = ''): number {
  if (!isSupportedCountry(country)) {
    return E164_MAX_DIGITS_TOTAL;
  }

  const countryCallingCode = getCountryCallingCode(country);
  const maxByE164 = Math.max(1, E164_MAX_DIGITS_TOTAL - countryCallingCode.length);
  const capToE164 = (n: number) => Math.min(Math.max(1, n), maxByE164);

  const lengths = getSortedPossibleNationalLengths(country);
  if (lengths.length === 0) {
    return capToE164(legacyMaxNationalFromTooLongProbe(country, maxByE164));
  }

  if (lengths.length === 1) {
    return capToE164(lengths[0]);
  }

  const minLen = lengths[0];
  const maxLen = lengths[lengths.length - 1];

  if (country === 'RU' || country === 'KZ') {
    if (partialNationalDigits.charAt(0) === '8') {
      return capToE164(maxLen);
    }
    return capToE164(minLen);
  }

  return capToE164(maxLen);
}

/**
 * Первая цифра национальной части (после country calling code), с которой номер ещё может
 * соответствовать метаданным страны (+7: RU и KZ — разные наборы).
 * Блокируем +75…, +76… и т.п. для РФ, чтобы не получать «сломанный» дальнейший ввод/валидацию.
 */
export function isPlausibleFirstNationalDigit(country: CountryCode, nationalDigits: string): boolean {
  if (!nationalDigits) return true;
  const d = nationalDigits[0];
  if (!/\d/.test(d)) return true;
  if (country === 'RU') {
    /** Продуктовый фокус на мобильные + формат 8… (14 цифр); городские 3/4/7 не вводим с клавиатуры. */
    return d === '8' || d === '9';
  }
  if (country === 'KZ') {
    return d === '7' || d === '8';
  }
  return true;
}

export function clampPhoneInput(phone: string, country?: CountryCode): string {
  const trimmed = phone.trim();
  if (!trimmed) return '';
  if (trimmed === '+') return '+';

  let digits = trimmed.replace(/\D/g, '');
  if (digits.length === 0) return trimmed.startsWith('+') ? '+' : '';

  if (country && isSupportedCountry(country)) {
    const countryCallingCode = getCountryCallingCode(country);
    const hasPrefix = digits.startsWith(countryCallingCode);
    let nationalDigits = hasPrefix ? digits.slice(countryCallingCode.length) : digits;

    if (nationalDigits.length > 0 && !isPlausibleFirstNationalDigit(country, nationalDigits)) {
      nationalDigits = '';
      digits = hasPrefix ? countryCallingCode : '';
    }

    const maxNationalLength = getMaxNationalLengthForCountry(country, nationalDigits);
    if (nationalDigits.length > maxNationalLength) {
      const clampedNational = nationalDigits.slice(0, maxNationalLength);
      digits = hasPrefix ? `${countryCallingCode}${clampedNational}` : clampedNational;
    }
  }

  let len = Math.min(digits.length, E164_MAX_DIGITS_TOTAL);
  while (len > 0) {
    const candidate = `+${digits.slice(0, len)}`;
    if (validatePhoneNumberLength(candidate) !== 'TOO_LONG') break;
    len -= 1;
  }
  if (len === 0) return '+';
  const plus = `+${digits.slice(0, len)}`;
  return parseIncompletePhoneNumber(plus) ?? plus;
}

/** Пустой или только +7: нужен префикс +79 для мобильного ввода (RU). */
export function shouldSeedRuMobileNationalNine(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  const cc = getCountryCallingCode('RU');
  if (digits.length === 0) return true;
  if (!digits.startsWith(cc)) return false;
  return digits.length === cc.length;
}

/** Если выбрана RU и национальная часть пуста — E.164 +79 (после клампа). Иначе `value` без изменений смысла. */
export function ensureRuMobileNineAfterCountryCodeIfEmpty(value: string, country: CountryCode): string {
  if (country !== 'RU') return value.trim();
  if (!shouldSeedRuMobileNationalNine(value)) return value.trim();
  return clampPhoneInput(`+${getCountryCallingCode('RU')}9`, 'RU');
}

/** Сколько цифр в отформатированной строке строго до позиции курсора (по символам). */
export function countDigitsBeforeCaret(formatted: string, caretCharIndex: number): number {
  let n = 0;
  const end = Math.min(Math.max(0, caretCharIndex), formatted.length);
  for (let i = 0; i < end; i++) {
    if (/\d/.test(formatted[i])) n += 1;
  }
  return n;
}

function digitIndicesRemovedByCharRange(
  formatted: string,
  selStart: number,
  selEnd: number
): { min: number; max: number } | null {
  let digitIndex = 0;
  let minI = Infinity;
  let maxI = -1;
  const a = Math.min(selStart, selEnd);
  const b = Math.max(selStart, selEnd);
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i])) {
      if (i >= a && i < b) {
        minI = Math.min(minI, digitIndex);
        maxI = Math.max(maxI, digitIndex);
      }
      digitIndex += 1;
    }
  }
  if (maxI < 0) return null;
  return { min: minI, max: maxI };
}

/**
 * RU и национальная часть с ведущей 9 (мобильный префикс): нельзя удалить эту первую 9.
 * `e164Value` — текущее значение поля (E.164 / частичный ввод из пропа).
 */
export function shouldBlockRuMobileLeadingNineRemoval(options: {
  country: CountryCode;
  e164Value: string;
  formattedValue: string;
  selectionStart: number;
  selectionEnd: number;
  inputType: string;
}): boolean {
  const { country, e164Value, formattedValue, selectionStart, selectionEnd, inputType } = options;
  if (country !== 'RU') return false;
  if (inputType !== 'deleteContentBackward' && inputType !== 'deleteContentForward') return false;

  const cc = getCountryCallingCode('RU');
  const valueDigits = e164Value.replace(/\D/g, '');
  if (!valueDigits.startsWith(cc)) return false;
  const national = valueDigits.slice(cc.length);
  if (national.length < 1 || national[0] !== '9') return false;

  const firstNatDigitIndex = cc.length;
  const selA = selectionStart ?? 0;
  const selB = selectionEnd ?? 0;

  if (selA !== selB) {
    const r = digitIndicesRemovedByCharRange(formattedValue, selA, selB);
    if (r && r.min <= firstNatDigitIndex && r.max >= firstNatDigitIndex) return true;
    return false;
  }

  if (inputType === 'deleteContentBackward') {
    const digitsBefore = countDigitsBeforeCaret(formattedValue, selA);
    const removedIdx = digitsBefore - 1;
    return removedIdx === firstNatDigitIndex;
  }

  const removedIdx = countDigitsBeforeCaret(formattedValue, selA);
  return removedIdx === firstNatDigitIndex;
}

/**
 * Значение из БД / старого UI → строка для react-phone-number-input (как после ввода).
 * Схлопывает «+7 999 …» в E.164, подтягивает 8…/10 цифр по стране, затем clamp.
 */
export function phoneValueForPicker(phone: string, defaultCountry: CountryCode = 'RU'): string {
  const trimmed = phone.trim();
  if (!trimmed) {
    if (defaultCountry === 'RU') {
      return clampPhoneInput(`+${getCountryCallingCode('RU')}9`, 'RU');
    }
    return '';
  }
  const parsed =
    parsePhoneNumberFromString(trimmed) ?? parsePhoneNumberFromString(trimmed, defaultCountry);
  const e164 = parsed ? parsed.format('E.164') : normalizePhone(trimmed, defaultCountry);
  return clampPhoneInput(e164, resolveCountryFromPhoneValue(e164, defaultCountry));
}

/** Текст кода страны для UI рядом с флагом (+7, +1, …). */
export function formatCountryCallingCode(country: CountryCode): string {
  if (!isSupportedCountry(country)) return '';
  return `+${getCountryCallingCode(country)}`;
}

export function getPhoneCountryOptions(locale = 'ru'): PhoneCountryOption[] {
  const language = locale.toLowerCase().startsWith('ru') ? 'ru' : 'en';
  const cached = phoneCountryOptionsCache.get(language);
  if (cached) return cached;

  const labels = phoneCountryLabels[language];

  const options = getCountries()
    .map((country) => ({
      value: country,
      label: labels[country] || country,
      callingCode: formatCountryCallingCode(country),
    }))
    .sort((a, b) => a.label.localeCompare(b.label, language));
  phoneCountryOptionsCache.set(language, options);
  return options;
}

/** Страна из E.164 / частичного ввода; иначе fallback (дефолт пикера). */
export function resolveCountryFromPhoneValue(phone: string, fallback: CountryCode): CountryCode {
  const t = phone.trim();
  if (!t) return fallback;
  const p = parsePhoneNumberFromString(t) ?? parsePhoneNumberFromString(t, fallback);
  const c = p?.country as CountryCode | undefined;
  return c && isSupportedCountry(c) ? c : fallback;
}

/**
 * Телефон для клиентов (онлайн-запись): в JSON настроек или fallback на телефон аккаунта мастера.
 * Иначе после регистрации по VK/TG в Master.phone есть номер, а в onlineBookingSettings.phone пусто —
 * форма «Контакты» и публичная страница показывали пусто.
 */
export function resolveOnlineBookingDisplayPhone(
  settingsPhone: unknown,
  masterPhone: string | null | undefined
): string {
  const fromSettings =
    typeof settingsPhone === 'string'
      ? settingsPhone.trim()
      : settingsPhone != null && String(settingsPhone).trim() !== ''
        ? String(settingsPhone).trim()
        : '';
  if (fromSettings) return fromSettings;
  return masterPhone != null ? String(masterPhone).trim() : '';
}

/**
 * Достаточно ли цифр, чтобы показывать ошибку формата (не дёргать на пустом/коротком вводе).
 * Полный UX: ошибку ещё привязывают к blur через родителя.
 */
export function isPhoneReadyForValidityCheck(phone: string, defaultCountry: CountryCode = 'RU'): boolean {
  const t = phone.trim();
  if (!t) return false;
  const digits = t.replace(/\D/g, '');
  if (digits.length < 8) return false;
  try {
    if (t.startsWith('+')) return isPossiblePhoneNumber(t);
    return isPossiblePhoneNumber(t, defaultCountry);
  } catch {
    return digits.length >= 10;
  }
}

/** ISO 3166-1 alpha-2 по языку интерфейса (дефолт страны в поле ввода). */
export function getDefaultCountryForLanguage(lang: string): CountryCode {
  const base = (lang.split(/[-_]/)[0] ?? 'en').toLowerCase();
  const map: Partial<Record<string, CountryCode>> = {
    ru: 'RU',
    en: 'US',
    uz: 'UZ',
    tg: 'TJ',
    ky: 'KG',
    az: 'AZ',
    hy: 'AM',
    kk: 'KZ',
    hi: 'IN',
  };
  return map[base] ?? 'US';
}

/** Шаблон маски телефона РФ: +7 (XXX) XXX-XX-XX. Только X как плейсхолдер (не 0). */
export const PHONE_MASK_TEMPLATE = '+7 (XXX) XXX-XX-XX';

/** Позиции цифр в шаблоне (индексы символов X в +7 (XXX) XXX-XX-XX) */
export const PHONE_MASK_DIGIT_POSITIONS: readonly number[] = [4, 5, 6, 9, 10, 11, 13, 14, 16, 17];

/** Максимум цифр после +7 */
export const PHONE_MASK_MAX_DIGITS = 10;

export type PhoneMaskConfig = {
  template: string;
  digitPositions: readonly number[];
  maxDigits: number;
};

/**
 * Конфигурация маски телефона РФ для masked input (mobile-master).
 */
export function getPhoneMaskConfig(): PhoneMaskConfig & { placeholder: string } {
  return {
    template: PHONE_MASK_TEMPLATE,
    placeholder: PHONE_MASK_TEMPLATE,
    digitPositions: PHONE_MASK_DIGIT_POSITIONS,
    maxDigits: PHONE_MASK_MAX_DIGITS,
  };
}

/**
 * Извлекает 10 цифр для маски ввода (убирает ведущие 7/8, чтобы не дублировать код страны)
 */
export function extractPhoneDigitsForMask(text: string): string {
  let digits = text.replace(/\D/g, '');
  if (!digits) return '';

  if (digits.length > 1 && (digits[0] === '7' || digits[0] === '8')) {
    if (digits[1] === '7' || digits[1] === '8') {
      digits = digits.slice(1);
    }
  }

  while (digits.length > 10 && (digits[0] === '8' || digits[0] === '7')) {
    digits = digits.slice(1);
  }

  return digits.slice(0, 10);
}

export function digitIndexToCaret(digitIndex: number, digitPositions: readonly number[]): number {
  if (digitIndex <= 0) return digitPositions[0] ?? 0;
  if (digitIndex >= digitPositions.length) return (digitPositions[digitPositions.length - 1] ?? 0) + 1;
  return digitPositions[digitIndex];
}

export function caretToDigitIndex(caretPos: number, digitPositions: readonly number[]): number {
  if (digitPositions.length === 0) return 0;
  if (caretPos <= digitPositions[0]) return 0;
  const last = digitPositions[digitPositions.length - 1];
  if (caretPos > last) return digitPositions.length;
  for (let i = 0; i < digitPositions.length; i++) {
    if (caretPos <= digitPositions[i]) return i;
  }
  return digitPositions.length - 1;
}

export function formatDigitsIntoTemplate(
  digits: string,
  template: string,
  digitPositions: readonly number[]
): string {
  const limited = digits.slice(0, digitPositions.length);
  const chars = template.split('');
  for (let i = 0; i < digitPositions.length; i++) {
    const pos = digitPositions[i];
    if (pos >= 0 && pos < chars.length) chars[pos] = limited[i] ?? template[pos];
  }
  return chars.join('');
}

export function formatPhoneForInput(value: string): string {
  const digits = extractPhoneDigitsForMask(value);
  if (digits.length === 0) return '';
  const { template, digitPositions } = getPhoneMaskConfig();
  return formatDigitsIntoTemplate(digits, template, digitPositions);
}

/**
 * Национальная часть для поля ввода (код страны показывается отдельно).
 * Формат как у react-phone-number-input / formatIncompletePhoneNumber (пробелы, не сырой digits).
 */
export function formatNationalPhoneForInput(
  nationalDigits: string,
  country: CountryCode = 'RU'
): string {
  const digits = nationalDigits.replace(/\D/g, '');
  if (!digits || !isSupportedCountry(country)) return digits;
  const callingCode = getCountryCallingCode(country);
  const formatted = formatIncompletePhoneNumber(`+${callingCode}${digits}`, country);
  const prefix = `+${callingCode}`;
  if (formatted.startsWith(prefix)) {
    return formatted.slice(prefix.length).trimStart();
  }
  return digits;
}

/** Placeholder без префикса кода страны, если dial code вынесен в отдельный слот. */
export function nationalPhonePlaceholderForInput(
  placeholder: string | undefined,
  country: CountryCode = 'RU'
): string | undefined {
  if (!placeholder?.trim() || !isSupportedCountry(country)) return placeholder;
  const prefix = formatCountryCallingCode(country);
  const trimmed = placeholder.trim();
  if (trimmed.startsWith(prefix)) {
    return trimmed.slice(prefix.length).trimStart();
  }
  return placeholder;
}

/**
 * Нормализует в E.164 при валидном номере; иначе — мягкий fallback для RU (миграции).
 */
export function normalizePhone(phone: string, defaultCountry: CountryCode = 'RU'): string {
  const trimmed = phone.trim();
  if (!trimmed) return '';

  if (trimmed.startsWith('+')) {
    try {
      const p = parsePhoneNumber(trimmed);
      if (p?.isValid()) return p.format('E.164');
    } catch {
      /* ignore */
    }
  }

  try {
    const p = parsePhoneNumber(trimmed, defaultCountry);
    if (p?.isValid()) return p.format('E.164');
  } catch {
    /* ignore */
  }

  const digits = trimmed.replace(/\D/g, '');
  if (digits.startsWith('8') && digits.length === 11) {
    return '+7' + digits.slice(1);
  }
  if (digits.startsWith('7') && digits.length === 11) {
    return '+' + digits;
  }
  if (digits.length === 10 && defaultCountry === 'RU') {
    return '+7' + digits;
  }

  if (trimmed.startsWith('+')) return trimmed;
  return digits || trimmed;
}

/**
 * Человекочитаемый международный формат (как в libphonenumber).
 */
export function formatPhone(
  phone: string | number | null | undefined,
  defaultCountry: CountryCode = 'RU'
): string {
  if (phone == null) return '';
  const raw = String(phone).trim();
  if (!raw) return '';

  try {
    const e164 = normalizePhone(raw, defaultCountry);
    if (!e164.startsWith('+')) return raw;
    const p = parsePhoneNumber(e164);
    if (p?.isValid()) return p.formatInternational();
  } catch {
    /* ignore */
  }
  return raw;
}

/**
 * Строгая валидация (полный номер для страны / E.164 с +).
 */
export function isValidPhone(phone: string, defaultCountry: CountryCode = 'RU'): boolean {
  const trimmed = phone.trim();
  if (!trimmed) return false;
  try {
    if (trimmed.startsWith('+')) {
      return isValidPhoneNumber(trimmed);
    }
    return isValidPhoneNumber(trimmed, defaultCountry);
  } catch {
    return false;
  }
}

export function isPossiblePhone(phone: string, defaultCountry: CountryCode = 'RU'): boolean {
  const trimmed = phone.trim();
  if (!trimmed) return false;
  try {
    if (trimmed.startsWith('+')) {
      return isPossiblePhoneNumber(trimmed);
    }
    return isPossiblePhoneNumber(trimmed, defaultCountry);
  } catch {
    return false;
  }
}

export function isPossibleAndValidPhone(phone: string, defaultCountry: CountryCode = 'RU'): boolean {
  return isPossiblePhone(phone, defaultCountry) && isValidPhone(phone, defaultCountry);
}

export function getPhoneDigits(phone: string): string {
  return phone.replace(/\D/g, '');
}
